/**
 * Settle with `promise`, or reject with `timeoutReason()` once `timeoutMs`
 * passes first (coding.md Rule 21). The timer is cleared as soon as the race
 * settles, so a fast call leaves no pending timer behind.
 *
 * The underlying work is NOT cancelled: it keeps running after the deadline,
 * and its late result or rejection is ignored. Each caller owns its deadline
 * and the reason it rejects with (an `Error`, a typed timeout error, or a
 * sentinel it compares against).
 *
 * Example:
 * ```ts
 * await withDeadline(
 *   sendEmail(message),
 *   SEND_TIMEOUT_MS,
 *   () => new Error(`send timed out after ${SEND_TIMEOUT_MS}ms`),
 * );
 * ```
 */
export async function withDeadline<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutReason: () => unknown,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(timeoutReason()), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}
